import React from "react";
import dataProvider from "../../../data/dataProvider";
import {
  validateNumber,
  check_url_telegram,
  validURL,
  check_url_whatsapp,
  is_empty,
} from "../../../utils/validations";
import {
  getAuthData,
  getCreatorDomain,
  getUserID,
  setAuthData,
} from "utils/AuthUtil";

import {
  SOCIAL,
  TEMPLATE_KEYS,
  CONTACT_MODE,
  TEMPLATE_TAGS,
  TemplateConfig,
} from "./constants";
import { api } from "data";
import { UPDATE_CREATOR_DATA_API } from "features/Common/modules/CreatorData/CreatorData.constants";
import { SECTION_SIDE_KEYS } from "./components/Sidebar/components/CreatorMiscellaneousData/CustomFooter/CustomFooter.constants";

// Generator function to break an array into chunks of a given size
export function* subArrayChunkGenerator(arr, chunkSize) {
  for (let i = 0; i < arr.length; i += chunkSize) {
    yield arr.slice(i, i + chunkSize);
  }
}

export const fetchCall = async ({
  url,
  method = "GET",
  payload,
  successCallback,
  errorCallback,
}) => {
  try {
    const { status, data } = await dataProvider.custom_request(
      url,
      method,
      payload
    );
    if (
      status === 200 &&
      successCallback &&
      typeof successCallback === "function"
    ) {
      return successCallback(data);
    }
    return data;
  } catch (error) {
    if (errorCallback && typeof errorCallback === "function") {
      return errorCallback(error);
    }
    return error;
  }
};

export const getCachedTags = () =>
  getAuthData()?.social_info?.template_tags || [];

export const fetchUserTags = async (successCallback = () => {}) => {
  return await fetchCall({
    url: `${api.get_creator_page_details}/${getUserID()}`,
    successCallback: (userDataProp) => {
      setAuthData(userDataProp);
      const userTags = userDataProp?.social_info?.template_tags;
      const tagList = TEMPLATE_TAGS.map((tag) => tag.slug);
      successCallback(
        userTags ? userTags.filter((t) => tagList.includes(t)) : userTags
      );
    },
  });
};

export const fetchUserAuth = async (successCallback = () => {}) => {
  return await fetchCall({
    url: `${api.get_creator_page_details}/${getUserID()}`,
    successCallback: (userDataProp) => {
      setAuthData(userDataProp);
      successCallback(userDataProp);
    },
  });
};

export const handleSaveTags = async ({ selectedTags, callback }) => {
  const authData = getAuthData();
  const newSocialInfo = {
    ...(authData?.social_info || {}),
    template_tags: selectedTags,
  };
  const newApiData = {
    source: "exly",
    updated_values: {
      social_info: newSocialInfo,
    },
  };
  const newCacheData = {
    ...authData,
    social_info: newSocialInfo,
  };

  await fetchCall({
    url: `${UPDATE_CREATOR_DATA_API}/${getUserID()}`,
    method: "POST",
    payload: newApiData,
  });

  setAuthData(newCacheData);

  if (callback && typeof callback === "function") {
    callback();
  }
};

export const getIframeURL = ({ templateName, themeName, subDomain }) =>
  templateName && themeName && subDomain
    ? `${
        window.location.protocol
      }//${subDomain}.${getCreatorDomain()}/?is_builder=true&preview=true&template=${templateName}&theme=${themeName}`
    : null;

export const isWebpageDetailsFormInvalid = ({
  stateProp,
  setError,
  pushNotif,
}) => {
  const submitErrorList = [];

  const socialInfo = { ...stateProp?.social_info };

  if ("website_heading" in socialInfo) {
    if (socialInfo?.website_heading?.length > 50) {
      submitErrorList.push({
        website_heading: {
          error: `Page Title must be under 50 characters (Currently ${socialInfo?.website_heading?.length})`,
          showError: true,
        },
      });
    }
  }

  if ("logo_text" in socialInfo) {
    if (socialInfo?.logo_text?.length > 50) {
      submitErrorList.push({
        logo_text: {
          error: `Logo text must be under 50 characters (Currently ${socialInfo?.logo_text?.length})`,
          showError: true,
        },
      });
    }
  }
  if ("interest_cta_text" in socialInfo) {
    if (socialInfo?.interest_cta_text?.length > 30) {
      submitErrorList.push({
        interest_cta_text: {
          error: `Get in Touch button text must be under 30 characters (Currently ${socialInfo?.interest_cta_text.length})`,
          showError: true,
        },
      });
    }
  }

  // The intro video is only applicable for classic template
  if (
    "video" in socialInfo &&
    stateProp.template.slug === TEMPLATE_KEYS.CLASSIC
  ) {
    if (socialInfo?.video?.length && !validURL(socialInfo?.video)) {
      submitErrorList.push({
        video: {
          error: "Invalid Intro Video URL",
          showError: true,
        },
      });
    }
  }

  if ("website_desc" in socialInfo && socialInfo?.website_desc?.length > 0) {
    if (socialInfo?.website_desc?.length < 2) {
      submitErrorList.push({
        website_desc: {
          error: "Short bio must be at least 2 characters",
          showError: true,
        },
      });
    } else if (socialInfo?.website_desc?.length > 200) {
      submitErrorList.push({
        website_desc: {
          error: `Short bio must be under 200 characters(around 25 words), (Currently ${socialInfo?.website_desc?.length})`,
          showError: true,
        },
      });
    }
  }

  Object.values(SOCIAL).forEach(({ accessor, validation }) => {
    const errKey = `social_${accessor}`;

    if (accessor in socialInfo) {
      const errVal = validation(socialInfo?.[accessor] || "");
      if (errVal) {
        submitErrorList.push({
          [errKey]: {
            error: errVal,
            showError: true,
          },
        });
      }
    }
  });

  for (const key in socialInfo?.follower_count) {
    if (is_empty(socialInfo?.follower_count[key])) {
      submitErrorList.push({
        [`follower_count_${key}`]: {
          error: `${key.split("_").join(" ")} follower count invalid`,
          showError: true,
        },
      });
    }
  }

  if (
    "phone_number" in stateProp &&
    validateNumber(stateProp.phone_number, stateProp.country_code)
  ) {
    submitErrorList.push({
      phone_number: { error: "Invalid phone number", showError: true },
    });
  }

  if ("contact_url" in socialInfo || "contact_mode" in socialInfo) {
    if (socialInfo?.contact_mode === CONTACT_MODE.WHATSAPP) {
      const whatsappErr =
        (socialInfo?.contact_url?.length === 0 &&
          "Please provide a WhatsApp URL") ||
        check_url_whatsapp(socialInfo?.contact_url);
      if (whatsappErr) {
        submitErrorList.push({
          contact_url: {
            error: whatsappErr,
            showError: true,
          },
        });
      }
    } else if (socialInfo?.contact_mode === CONTACT_MODE.TELEGRAM) {
      const telegramErr =
        (socialInfo?.contact_url?.length === 0 &&
          "Please provide a Telegram URL") ||
        check_url_telegram(socialInfo?.contact_url);
      if (telegramErr) {
        submitErrorList.push({
          contact_url: { error: telegramErr, showError: true },
        });
      }
    }
  }

  if ("display_name" in stateProp) {
    const fullName = stateProp?.display_name?.trim?.();
    if (fullName?.length > 50) {
      submitErrorList.push({
        display_name: {
          error: `Full name text should be less than 50 characters (Currently ${fullName?.length})`,
          showError: true,
        },
      });
    } else if (fullName?.length === 0) {
      submitErrorList.push({
        display_name: { error: "A name is required", showError: true },
      });
    } else if (fullName?.length < 3) {
      submitErrorList.push({
        display_name: {
          error: `Full name must be at least 3 characters (Currently ${fullName?.length})`,
          showError: true,
        },
      });
    }
  }

  if (submitErrorList.length > 0) {
    setError(Object.assign({}, ...submitErrorList));

    pushNotif(
      <>
        Please correct the following errors:
        <br />
        {Object.values(submitErrorList.map(Object.values))
          .map((p) => p?.[0]?.error)
          .filter((p) => p)
          .map((errString) => (
            <>
              - {errString}
              <br />
            </>
          ))}
      </>,
      {
        color: "coral_red",
      }
    );
    return true;
  }

  return false;
};

/**
 * @returns `IHost.IStore` of `exly-webapp` repo
 */
export const transformStateForPreview = ({
  stateProp,
  testimonialData,
  galleryData,
  featuresData,
  statisticsData,
}) => {
  const hostData = {
    ...stateProp,
    name: `${stateProp.display_name}`,
    phone_number: stateProp.phone_number,

    contact_mode: stateProp?.social_info?.contact_mode,
    contact_url: stateProp?.social_info?.contact_url,

    country_code: stateProp?.country_code,
    profile_pic: stateProp?.display_image,
    cover_pic: stateProp?.cover_image,

    ...("interest_cta_text" in (stateProp?.social_info || {}) && {
      interest_cta_text: stateProp?.social_info?.interest_cta_text,
    }),
    intro_video: stateProp?.social_info?.video,
    title: stateProp?.social_info?.website_heading,
    short_description: stateProp?.social_info?.website_desc,
    description: stateProp?.bio_text,

    testimonials: {
      title: stateProp?.custom_headings?.testimonials_title || "Testimonials",
      data: testimonialData,
    },
    gallery: {
      title: stateProp?.custom_headings?.gallery_title || "Portfolio",
      data: galleryData,
    },
    features: {
      title: stateProp?.custom_headings?.features_title || "Top Features",
      data: featuresData,
    },
    statistics: {
      title: stateProp?.custom_headings?.statistics_title || "Company Matrix",
      data: statisticsData,
    },
    expand_host_desc: stateProp?.lead_keys?.expand_host_desc || false,

    ...((stateProp?.about_title || stateProp?.lead_keys?.about_title) && {
      about_title:
        stateProp?.about_title || stateProp?.lead_keys?.about_title || "About",
    }),
    ...(stateProp?.lead_keys?.offerings_title && {
      offerings_title: stateProp?.lead_keys?.offerings_title,
    }),
    ...(stateProp?.lead_keys?.custom_team_navbar_title && {
      custom_team_navbar_title: stateProp?.lead_keys?.custom_team_navbar_title,
    }),
    ...(stateProp?.lead_keys?.custom_team_section_title && {
      custom_team_section_title:
        stateProp?.lead_keys?.custom_team_section_title,
    }),
  };

  return {
    dataHost: { profile_data: hostData },
    hostName: stateProp?.sub_domain,
    theme: {
      colors: stateProp?.colors,
    },
    font: stateProp?.font,
  };
};

export const parseUsername = (socialKey, socialUrl) => {
  if (!socialUrl || !socialUrl.includes(socialKey)) return "";

  return socialUrl?.split(`${socialKey}.com/`)?.[1];
};

// https://stackoverflow.com/a/5624139
export const rgbToHex = (rgb) => {
  const [r, g, b] = rgb.split(",").map((p) => parseInt(p.trim()));
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
    result[3],
    16
  )}`;
};

function checkProperties(objectToCheck, SIDEBAR_KEYS) {
  let isNull = false;
  Object.keys(objectToCheck).map((key) => {
    if (
      objectToCheck[key] == null &&
      ![SIDEBAR_KEYS.LOGO, SIDEBAR_KEYS.COLORS].includes(key)
    ) {
      isNull = true;
    }
  });
  return isNull;
}

export const isStepsChecked = (
  initialState,
  SIDEBAR_KEYS,
  testimonialData,
  galleryData,
  featuresData,
  miscellaneous_data,
  statisticsData
) => {
  const checkMiscellaneousEmpty = (type) => {
    return (
      !is_empty(miscellaneous_data?.[type]) &&
      !is_empty(miscellaneous_data?.[type][0]?.data?.data)
    );
  };

  const checkCustomFooterEmpty = (type) => {
    return (
      !is_empty(miscellaneous_data?.[type]) &&
      !is_empty(miscellaneous_data?.[type][0]?.data) &&
      (!is_empty(
        miscellaneous_data?.[type][0]?.data?.[
          SECTION_SIDE_KEYS.left_section.key
        ]
      ) ||
        !is_empty(
          miscellaneous_data?.[type][0]?.data?.[
            SECTION_SIDE_KEYS.right_section.key
          ]
        ))
    );
  };

  // state accessor keys for social fields
  const socialAccessors = Object.values(SOCIAL).map((s) => s.accessor);
  const checkStatus = {
    [SIDEBAR_KEYS.SUBDOMAIN]: initialState?.sub_domain?.length > 0,
    [SIDEBAR_KEYS.HEADER]:
      initialState?.social_info?.logo_text ||
      initialState?.social_info?.logo_image,
    [SIDEBAR_KEYS.COVER]: initialState?.social_info?.website_heading,
    [SIDEBAR_KEYS.ABOUT_YOU]:
      initialState?.bio_text &&
      initialState?.display_image &&
      initialState?.display_name,
    [SIDEBAR_KEYS.SOCIAL]: Object.entries(initialState?.social_info)
      .filter(([k]) => socialAccessors.includes(k))
      .some(([, v]) => v), // At least one should be non-empty
    [SIDEBAR_KEYS.CONTACT_DETAILS]:
      initialState?.social_info?.contact_mode !== "none",
    [SIDEBAR_KEYS.TESTIMONIALS]: testimonialData?.length > 0,
    [SIDEBAR_KEYS.GALLERY]: galleryData?.length > 0,
    [SIDEBAR_KEYS.FEATURES]: featuresData?.length > 0,
    [SIDEBAR_KEYS.LOGO]: initialState?.social_info?.logo_image,
    [SIDEBAR_KEYS.COLORS]: true,
    [SIDEBAR_KEYS.AWARDS]: checkMiscellaneousEmpty("awards"),
    [SIDEBAR_KEYS.CUSTOM_FOOTER]: checkCustomFooterEmpty("customFooter"),
    [SIDEBAR_KEYS.BRAND_COLLABORATIONS]: checkMiscellaneousEmpty("brands"),
    [SIDEBAR_KEYS.MEDIA_MENTIONS]: checkMiscellaneousEmpty("media"),
    [SIDEBAR_KEYS.OFFERINGS_SECTION]:
      checkMiscellaneousEmpty("offeringsSection"),
    [SIDEBAR_KEYS.INTRO_VIDEO]: checkMiscellaneousEmpty("introVideo"),
    [SIDEBAR_KEYS.BLOGS]: checkMiscellaneousEmpty("blogs"),
    [SIDEBAR_KEYS.TEAMS]: checkMiscellaneousEmpty("teams"),
    [SIDEBAR_KEYS.ADDITIONAL_POINTERS]:
      checkMiscellaneousEmpty("additionalPointers"),
    [SIDEBAR_KEYS.ABOUT_SECTION]:
      initialState?.social_info?.about_section_title || initialState?.bio_text,
    [SIDEBAR_KEYS.STATISTICS]: !is_empty(statisticsData?.length),
  };
  return [checkStatus, checkProperties(checkStatus, SIDEBAR_KEYS)];
};

/**
 * @description visit https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/RegExp
 * for more info
 * @param {*} regexStr regular expression string
 * @param {*} flags regular expression flags
 * @returns RegExp Object composed from the regular expression string
 */
export const getRegexObjectFromString = (regexStr, flags) => {
  if (is_empty(regexStr)) return null;
  else return new RegExp(regexStr, flags);
};

/**
 * Calculates and returns the parameters for a background image based on the provided width,
 * template key, and the state of the application. The function considers the template
 * configuration and cover image specifications to determine the full width, height, and
 * base aspect ratio of the background image.
 *
 * @param {Object} state - The current state of the application, including template information.
 * @param {string} template_key - The key identifying the template (if available).
 * @param {number} width - The desired width of the background image.
 * @returns {Object} - An object containing the calculated parameters:
 *                    - fullWidth: The recommended full width of the background image.
 *                    - width: The provided width of the background image.
 *                    - height: The calculated height based on the width and aspect ratio.
 *                    - baseAspectRatio: The calculated base aspect ratio of the image.
 */
export const getBackgroundImageParams = (state = {}, template_key, width) => {
  // Ensure that TemplateConfig and coverImage properties are available
  if (!state.template?.slug && !template_key) {
    console.warn(
      "Template key is not provided. Unable to calculate image parameters."
    );
    return {};
  }

  // Retrieve template-specific configuration
  const templateConfig = TemplateConfig[state.template?.slug || template_key];

  // Check if templateConfig and coverImage are available
  if (!templateConfig || !templateConfig.coverImage) {
    console.warn(
      "Template configuration or coverImage information not available."
    );
    return {};
  }

  // Retrieve coverImage specifications
  const coverImage = templateConfig.coverImage;

  // Check if coverImage.width and coverImage.aspect_ratio are available
  if (!coverImage || !coverImage.width || !coverImage.aspect_ratio) {
    console.warn(
      "Cover image width or aspect ratio information not available."
    );
    return {};
  }

  // Calculate height and base aspect ratio based on the provided width
  const fullWidth = coverImage.width;
  const height = width / coverImage.aspect_ratio;
  const baseAspectRatio = width / height;

  return { fullWidth, width, height, baseAspectRatio };
};
